import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Home, KeyRound, ShoppingCart, Inbox, Users, Headset, UserCircle, ClipboardList, ClipboardCheck } from "lucide-react";
import { AppShell, LocaleProvider, Loading, LoginPage, MeContext, applyTenantBrand, hasPermission, useApi, useLogout, useMe, useT, type NavItem } from "@iga/ui";
import { useCampaigns, useTasks } from "./lib/hooks";
import { LocaleSettingContext, detectLocale, normaliseLocale, storeLocale, storedLocale, type PortalLocale } from "./lib/locale";
import { AvatarMenu } from "./components/AvatarMenu";
import { HomePage } from "./pages/Home";
import { TasksPage } from "./pages/Tasks";
import { Signup } from "./pages/Signup";
import { SignupVerify } from "./pages/SignupVerify";
import { MyAccessPage } from "./pages/MyAccess";
import { RequestAccessPage } from "./pages/RequestAccess";
import { MyRequestsPage, RequestDetailPage } from "./pages/MyRequests";
import { ReviewsPage, ReviewCampaignPage } from "./pages/Review";
import { TeamPage, TeamMemberPage } from "./pages/Team";
import { ServiceDeskPage, ServiceDeskIdentityPage } from "./pages/ServiceDesk";
import { ProfilePage } from "./pages/Profile";

/** Public part of GET /api/auth/providers: brand and default locale for every visitor, signed in or not. */
interface PublicTenantInfo { tenant: { slug: string; displayName: string }; branding?: { productName?: string; primaryColor?: string | null; logoUrl?: string | null }; locale?: { default?: string; timeZone?: string } }

function LegacyRedirect({ to }: { to: (p: Record<string, string | undefined>) => string }) {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
}

function Shell() {
  const t = useT();
  const me = useMe();
  const logout = useLogout();
  const tasks = useTasks();
  const m = me.data!;
  const perms = m.user.permissions;
  const isSupport = hasPermission(perms, "identities:read");
  const campaigns = useCampaigns(!!m.identity);
  const tk = tasks.data;
  const openCampaigns = campaigns.data?.items.filter((c) => c.pending > 0).length ?? 0;
  const taskCount = (tk?.approvals ?? 0) + openCampaigns + (tk?.detail?.requestsFailed ?? 0);
  const brand = useContext(BrandContext);
  const brandName = brand?.productName || m.tenant.displayName;

  const items: NavItem[] = [
    { to: "/", label: t("p.nav.home"), icon: <Home className="h-5 w-5" aria-hidden="true" />, end: true },
    { to: "/tasks", label: t("p.nav.tasks"), icon: <Inbox className="h-5 w-5" aria-hidden="true" />, badge: taskCount || undefined, badgeTone: tk?.blocked || tk?.needsAttention ? "warn" : "brand" },
    { to: "/request", label: t("p.nav.request"), icon: <ShoppingCart className="h-5 w-5" aria-hidden="true" /> },
    { to: "/my-access", label: t("p.nav.myAccess"), icon: <KeyRound className="h-5 w-5" aria-hidden="true" /> },
    ...(m.isManager ? [{ to: "/team", label: t("p.nav.team"), icon: <Users className="h-5 w-5" aria-hidden="true" /> }] : []),
    ...(isSupport ? [{ to: "/servicedesk", label: t("p.nav.servicedesk"), icon: <Headset className="h-5 w-5" aria-hidden="true" /> }] : []),
  ];
  const more: NavItem[] = [
    { to: "/requests", label: t("p.nav.myRequests"), icon: <ClipboardList className="h-5 w-5" aria-hidden="true" /> },
    { to: "/review", label: t("p.nav.reviews"), icon: <ClipboardCheck className="h-5 w-5" aria-hidden="true" /> },
    { to: "/profile", label: t("p.nav.profile"), icon: <UserCircle className="h-5 w-5" aria-hidden="true" /> },
  ];
  // Phone tab bar (§5): Home · Tasks · Request · My access (or Service desk for support) · More
  const bottom: NavItem[] = [
    items[0]!, { ...items[1]!, label: t("p.nav.tasks") }, { ...items[2]!, label: t("p.nav.requestShort") },
    isSupport ? items.find((i) => i.to === "/servicedesk")! : items[3]!,
    ...(isSupport ? [items[3]!] : []),
    ...(m.isManager ? [items.find((i) => i.to === "/team")!] : []),
    ...more,
  ];

  return (
    <MeContext.Provider value={m}>
      <AppShell variant="portal" brand={<span>{brandName}</span>} name={brandName} logoUrl={brand?.logoUrl} sections={[{ items }]} bottomItems={bottom}
        userMenu={<AvatarMenu user={{ displayName: m.user.displayName, email: m.user.email }} onLogout={() => void logout()} />} onLogout={() => void logout()}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/my-access" element={<MyAccessPage />} />
          <Route path="/request" element={<RequestAccessPage />} />
          <Route path="/requests" element={<MyRequestsPage />} />
          <Route path="/requests/:id" element={<RequestDetailPage />} />
          <Route path="/review" element={<ReviewsPage />} />
          <Route path="/review/:campaignId" element={<ReviewCampaignPage />} />
          {m.isManager && <Route path="/team" element={<TeamPage />} />}
          {m.isManager && <Route path="/team/:identityId" element={<TeamMemberPage />} />}
          {isSupport && <Route path="/servicedesk" element={<ServiceDeskPage />} />}
          {isSupport && <Route path="/servicedesk/:id" element={<ServiceDeskIdentityPage />} />}
          <Route path="/profile" element={<ProfilePage />} />
          {/* Links in older e-mails and bookmarks */}
          <Route path="/approvals" element={<Navigate to="/tasks?source=approvals" replace />} />
          <Route path="/approvals/:id" element={<LegacyRedirect to={(p) => `/requests/${p.id}`} />} />
          <Route path="/my-requests" element={<Navigate to="/requests" replace />} />
          <Route path="/my-requests/:id" element={<LegacyRedirect to={(p) => `/requests/${p.id}`} />} />
          <Route path="/certifications" element={<Navigate to="/review" replace />} />
          <Route path="/certifications/:campaignId" element={<LegacyRedirect to={(p) => `/review/${p.campaignId}`} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </MeContext.Provider>
  );
}

/**
 * Language and tenant brand around the whole app, from the public GET /api/auth/providers (same query key as the
 * shared LoginPage, so it is fetched once). Precedence for the language: ?lang → stored choice → tenant default → browser.
 */
function LocaleAndBrand({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<PortalLocale>(() => detectLocale());
  const info = useApi<PublicTenantInfo>(["auth-providers"], "/api/auth/providers", undefined, { retry: false, staleTime: Infinity });
  useEffect(() => {
    if (!info.data) return;
    applyTenantBrand({ primaryColor: info.data.branding?.primaryColor, logoUrl: info.data.branding?.logoUrl });
    const tenantLocale = normaliseLocale(info.data.locale?.default);
    if (tenantLocale && !storedLocale()) setLocaleState(tenantLocale);
  }, [info.data]);
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  const value = useMemo(() => ({ locale, setLocale: (l: PortalLocale) => { storeLocale(l); setLocaleState(l); } }), [locale]);
  return (
    <LocaleSettingContext.Provider value={value}>
      <LocaleProvider value={locale}><BrandContext.Provider value={info.data?.branding ?? null}>{children}</BrandContext.Provider></LocaleProvider>
    </LocaleSettingContext.Provider>
  );
}

const BrandContext = createContext<PublicTenantInfo["branding"] | null>(null);

export function App() {
  const me = useMe();
  const loc = useLocation();
  let body: ReactNode;
  if (loc.pathname === "/signup") body = <Signup />;
  else if (loc.pathname === "/signup/verify") body = <SignupVerify />;
  else if (me.isLoading) body = <Loading />;
  else if (!me.data) body = <BrandedLogin returnTo={loc.pathname + loc.search} />;
  else body = <Shell />;
  return <LocaleAndBrand>{body}</LocaleAndBrand>;
}

/** Login page with the tenant logo (brand colour and tint come from the CSS variables applyTenantBrand sets). */
function BrandedLogin({ returnTo }: { returnTo: string }) {
  const brand = useContext(BrandContext);
  return <LoginPage returnTo={returnTo} logoUrl={brand?.logoUrl} />;
}
